import { useForm } from '@tanstack/react-form';
import { Button } from '@sara/ui/components/button';
import { Field, FieldContent, FieldError, FieldLabel } from '@sara/ui/components/field';
import { Input } from '@sara/ui/components/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@sara/ui/components/select';
import { inviteMemberFormSchema, type InviteMemberFormValues } from './schemas';

interface InviteMemberFormProps {
  onSubmit: (values: InviteMemberFormValues) => void;
  onCancel: () => void;
  isPending: boolean;
  error?: string | null;
}

export function InviteMemberForm({ onSubmit, onCancel, isPending, error }: InviteMemberFormProps) {
  const form = useForm({
    defaultValues: { email: '', role: 'MEMBER' } as InviteMemberFormValues,
    validators: { onSubmit: inviteMemberFormSchema },
    onSubmit: ({ value }) => onSubmit(value),
  });

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        e.stopPropagation();
        void form.handleSubmit();
      }}
      className="space-y-5"
      autoComplete="off"
      // zod owns validation — native email constraint validation would block
      // the submit event before the schema ever runs
      noValidate
    >
      <form.Field name="email">
        {(field) => {
          const hasError = field.state.meta.errors.length > 0;
          return (
            <Field data-invalid={hasError || undefined}>
              <FieldLabel htmlFor={field.name} className="text-[13px] font-medium text-foreground">
                Email
              </FieldLabel>
              <FieldContent>
                <Input
                  id={field.name}
                  type="email"
                  placeholder="teammate@company.com"
                  value={field.state.value}
                  onBlur={field.handleBlur}
                  onChange={(e) => field.handleChange(e.target.value)}
                  disabled={isPending}
                  aria-invalid={hasError}
                  autoComplete="off"
                  data-testid="invite-email-input"
                />
                <FieldError errors={field.state.meta.errors} />
              </FieldContent>
            </Field>
          );
        }}
      </form.Field>

      <form.Field name="role">
        {(field) => (
          <Field>
            <FieldLabel htmlFor={field.name} className="text-[13px] font-medium text-foreground">
              Role
            </FieldLabel>
            <FieldContent>
              <Select
                value={field.state.value}
                onValueChange={(role) => field.handleChange(role as InviteMemberFormValues['role'])}
                disabled={isPending}
              >
                <SelectTrigger id={field.name} className="w-full" data-testid="invite-role-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="MEMBER">Member</SelectItem>
                  <SelectItem value="ADMIN">Admin</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                Admins can manage the team, API keys, and organization settings. Members get
                everything else.
              </p>
            </FieldContent>
          </Field>
        )}
      </form.Field>

      {error && (
        <div className="rounded-xl border border-destructive/40 bg-destructive/8 p-3 text-sm text-destructive">
          {error}
        </div>
      )}

      <div className="flex justify-end gap-2 pt-2">
        <Button
          type="button"
          variant="outline"
          shape="pill"
          size="sm"
          onClick={onCancel}
          disabled={isPending}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          variant="tinted"
          shape="pill"
          size="sm"
          className="gap-1.5"
          disabled={isPending}
          data-testid="invite-submit"
        >
          {isPending ? 'Inviting…' : 'Send invite'}
        </Button>
      </div>
    </form>
  );
}
